import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { MissionSpec, MissionVersion } from './workspaceApi';

const sections = [
  { key: 'name', label: 'Name' },
  { key: 'brief', label: 'Brief' },
  { key: 'trigger', label: 'Trigger' },
  { key: 'actions', label: 'Actions and campaign scope' },
] as const;
const describe = (spec: MissionSpec, key: (typeof sections)[number]['key']) => {
  if (key === 'trigger')
    return [
      spec.trigger.kind,
      spec.trigger.cron,
      spec.trigger.timezone,
      spec.trigger.filters
        ? `${spec.trigger.filters.mode === 'all' ? 'All' : 'Any'} conditions: ${spec.trigger.filters.conditions.map((condition) => `${condition.field} ${condition.operator}${condition.operator === 'exists' ? '' : ` ${condition.value}`}`).join('; ') || 'Every ended call'}`
        : undefined,
    ]
      .filter(Boolean)
      .join(' · ');
  if (key === 'actions')
    return spec.actions
      .map(
        (action) =>
          `${action.kind} · Campaign ${action.campaignId}${action.callId ? ` · Call ${action.callId}` : ''}${action.title !== undefined ? ` · New name: ${action.title}` : ''}`,
      )
      .join('\n');
  return spec[key] || '—';
};

export const MissionVersionHistory = ({
  versions,
  activeVersionId,
  draft,
  canRestore,
  onRestore,
}: {
  versions: MissionVersion[];
  activeVersionId?: string | null;
  draft?: MissionSpec;
  canRestore: boolean;
  onRestore: (version: MissionVersion) => void;
}) => {
  const [selected, setSelected] = useState<MissionVersion | null>(null);
  return (
    <Stack gap={2}>
      <Box>
        <Typography variant="h6">Published versions</Typography>
        <Typography variant="body2" color="text.secondary">
          Review a saved version or restore it into the draft. Publishing is a separate action.
        </Typography>
      </Box>
      {!versions.length ? (
        <Typography color="text.secondary">No published version yet.</Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table aria-label="Published mission versions" sx={{ minWidth: 550 }}>
            <TableHead>
              <TableRow>
                {['Version', 'Published at', 'Mission', ''].map((label, index) => (
                  <TableCell key={index}>{label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {versions.map((version) => (
                <TableRow key={version.id}>
                  <TableCell>
                    <Stack direction="row" gap={1} alignItems="center">
                      <Typography variant="body2">Version {version.number}</Typography>
                      {version.id === activeVersionId && (
                        <Chip label="Current" variant="outlined" size="small" />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>{new Date(version.created_at).toLocaleString()}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {version.spec.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {describe(version.spec, 'trigger')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" gap={1}>
                      <Button
                        aria-label={`View version ${version.number}`}
                        onClick={() => setSelected(version)}>
                        View version
                      </Button>
                      <Button disabled={!canRestore} onClick={() => onRestore(version)}>
                        Restore into draft
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Dialog
        open={!!selected}
        onClose={() => setSelected(null)}
        fullWidth
        maxWidth="md"
        aria-labelledby="mission-version-title">
        <DialogTitle id="mission-version-title">
          Version {selected?.number} · Compare with saved draft
        </DialogTitle>
        <DialogContent>
          <Stack gap={2}>
            <Typography variant="body2" color="text.secondary">
              This compares the published version with the last saved draft. Unsaved edits stay in
              the editor.
            </Typography>
            {selected &&
              sections.map(({ key, label }) => (
                <Paper variant="outlined" key={key} sx={{ p: 2 }}>
                  <Stack direction="row" gap={1} alignItems="center" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">{label}</Typography>
                    {draft && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={
                          JSON.stringify(selected.spec[key]) === JSON.stringify(draft[key])
                            ? 'Unchanged'
                            : 'Changed'
                        }
                      />
                    )}
                  </Stack>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      gap: 3,
                    }}>
                    {[
                      { name: 'Published version', spec: selected.spec },
                      { name: 'Saved draft', spec: draft },
                    ].map(({ name, spec }) => (
                      <Box key={name} sx={{ minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary">
                          {name}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', mt: 1 }}>
                          {spec ? describe(spec, key) : 'No saved draft'}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Paper>
              ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)}>Close version</Button>
          <Button
            disabled={!canRestore}
            onClick={() => {
              if (selected) onRestore(selected);
              setSelected(null);
            }}>
            Restore into draft
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
